import React from 'react'
import { Link } from 'react-router-dom'
import { Shield, Mail, Phone, MapPin, ExternalLink } from 'lucide-react'

const Footer = () => {
  const currentYear = new Date().getFullYear()

  const quickLinks = [
    { label: 'Home', path: '/' },
    { label: 'Assessment', path: '/assessment' },
    { label: 'Policy Library', path: '/policy-library' },
    { label: 'Learning Center', path: '/learning-center' },
    { label: 'About', path: '/about' }
  ]

  const resources = [
    { label: 'DICT Official Website', url: 'https://dict.gov.ph', external: true },
    { label: 'Data Privacy Act', url: 'https://privacy.gov.ph', external: true },
    { label: 'Cybersecurity Framework', url: '#', external: false },
    { label: 'Government IT Standards', url: '#', external: false },
    { label: 'Contact Support', path: '/contact' }
  ]

  const governmentAgencies = [
    { name: 'DICT', description: 'Department of Information and Communications Technology' },
    { name: 'NPC', description: 'National Privacy Commission' },
    { name: 'CICC', description: 'Cybercrime Investigation and Coordinating Center' },
    { name: 'BSP', description: 'Bangko Sentral ng Pilipinas' }
  ]

  return (
    <footer className="bg-gov-800 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex items-center justify-center w-10 h-10 bg-cyber-600 rounded-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-display font-bold">
                  CyberGov <span className="text-cyber-400">Check</span>
                </h3>
                <p className="text-xs text-gov-300">Cybersecurity Assessment</p>
              </div>
            </div>
            <p className="text-gov-300 text-sm mb-4">
              Empowering Philippine government offices with simple, effective cybersecurity assessment tools and resources.
            </p>
            <div className="flex space-x-2">
              <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
              <div className="w-3 h-3 bg-red-600 rounded-full"></div>
              <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-cyber-400">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gov-300 hover:text-cyber-400 transition-colors duration-200 text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-cyber-400">Resources</h4>
            <ul className="space-y-2">
              {resources.map((resource, index) => (
                <li key={index}>
                  {resource.external ? (
                    <a
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gov-300 hover:text-cyber-400 transition-colors duration-200 text-sm flex items-center space-x-1"
                    >
                      <span>{resource.label}</span>
                      <ExternalLink size={12} />
                    </a>
                  ) : resource.path ? (
                    <Link
                      to={resource.path}
                      className="text-gov-300 hover:text-cyber-400 transition-colors duration-200 text-sm"
                    >
                      {resource.label}
                    </Link>
                  ) : (
                    <span className="text-gov-300 text-sm">{resource.label}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Government Agencies */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-cyber-400">Government Partners</h4>
            <div className="space-y-3 mb-6">
              {governmentAgencies.map((agency, index) => (
                <div key={index} className="text-sm">
                  <p className="font-medium text-white">{agency.name}</p>
                  <p className="text-gov-400 text-xs">{agency.description}</p>
                </div>
              ))}
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-sm text-gov-300">
                <Mail size={14} />
                <span>support@cybergov.gov.ph</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gov-300">
                <Phone size={14} />
                <span>+63 (02) 8888-DICT</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gov-300">
                <MapPin size={14} />
                <span>Quezon City, Philippines</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gov-700 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-gov-400">
              <p>
                © {currentYear} CyberGov Check. Developed for the Philippine Government.
              </p>
              <p className="mt-1">
                In partnership with DICT and Local Government Units.
              </p>
            </div>
            
            <div className="flex space-x-6 text-sm">
              <Link to="/privacy" className="text-gov-400 hover:text-cyber-400 transition-colors duration-200">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gov-400 hover:text-cyber-400 transition-colors duration-200">
                Terms of Service
              </Link>
              <Link to="/accessibility" className="text-gov-400 hover:text-cyber-400 transition-colors duration-200">
                Accessibility
              </Link>
            </div>
          </div>
          
          <div className="mt-4 text-center">
            <p className="text-xs text-gov-500">
              🇵🇭 Securing the Digital Philippines - Building Cyber Resilient Communities
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer